import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {TemplateIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import ConfirmModal from "components/ConfirmModal";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "context/AuthContext";
import {QuizType} from "../../../types/QuizType";
import QuizTypeContextMenu from "../../../components/menus/QuizTypeContextMenu";

// Component ----

function QuizTypeList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['quiz-types.all', {page}],
    () => ngage.quizTypes.all(page),
    // TODO: Add exhibit type permissions
    {keepPreviousData: true, enabled: auth.can('exhibit-types:read')}
  )

  const [deletingQuizType, setDeletingQuizType] = useState<QuizType | null>(null);
  const {mutateAsync: deleteQuizType} = useMutation(ngage.quizTypes.destroy)

  // Prepare action handlers
  async function handleDeleteQuizType(quizType: QuizType) {
    if (quizType.id) {
      try {
        await deleteQuizType(quizType.id)

        setDeletingQuizType(null)
        notify("success", `${termFor('Exhibit Type')} Deleted`, `${quizType.name} has been permanently removed`)

        return invalidate('exhibit-types.all')
      } catch (error) {
        setDeletingQuizType(null)
        notify("error", `${termFor('Exhibit Type')} Not Deleted`, `${quizType.name} could not be removed`)
      }
    }
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('quiz-types.all', page, ngage.quizTypes.all)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('quiz-types:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/quiz-types/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'App Content': '/',
    'Interactive': '/',
    [termFor('Quiz Types')]: '/quiz-types'
  }

  return (
    <Page
      title={termFor('Quiz Types')}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Quizzes')}</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <TemplateIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('quiz types')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((quizType, index) => (
              <TableRow key={quizType.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <QuizTypeContextMenu
                        quizType={quizType}
                        onDeleteQuizType={() => setDeletingQuizType(quizType)}
                        deleteDisabled={Number(quizType.quiz_count) > 0}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      {quizType.name}
                    </div>
                  </div>
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{quizType.quiz_count}</TableData>
                <TableData>{formattedDateTime(quizType.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <QuizTypeContextMenu
                    quizType={quizType}
                    onDeleteQuizType={() => setDeletingQuizType(quizType)}
                    deleteDisabled={Number(quizType.quiz_count) > 0}
                  />
                </TableData>

                {/* Delete modal */}
                <ConfirmModal
                  show={deletingQuizType === quizType}
                  type={ConfirmModal.types.DANGER}
                  title={`Delete ${termFor('Quiz Type')}`}
                  body={`Are you sure you want to permanently delete '${quizType.name}'? This action cannot be undone.`}
                  approveLabel="Delete"
                  onApprove={() => handleDeleteQuizType(quizType)}
                  onCancel={() => setDeletingQuizType(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default QuizTypeList;